"use client";

import { cn } from "@/lib/utils";
import { type ButtonHTMLAttributes } from "react";

type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg" | "touch";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--brand-primary)] text-[var(--brand-on-primary,#fff)] shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.14)] hover:bg-[var(--brand-primary-hover)] active:bg-[var(--brand-primary-hover)] disabled:opacity-50",
  secondary:
    "bg-white border border-[rgba(0,0,0,0.10)] text-[#374151] shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:bg-gray-50 hover:border-[rgba(0,0,0,0.14)] disabled:opacity-50",
  outline:
    "bg-transparent border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5 disabled:opacity-50",
  ghost:
    "bg-transparent text-[#6B7280] hover:bg-black/[0.04] hover:text-[#111827] disabled:opacity-50",
  danger:
    "bg-red-500 text-white shadow-[0_1px_2px_rgba(0,0,0,0.08)] hover:bg-red-600 disabled:opacity-50",
};

// Tinggi desktop dipertahankan (h-7/8/9) supaya densitas panel admin tidak
// berubah, tapi di bawah `md` semuanya dinaikkan ke 44px — WCAG 2.5.8 dan
// ergonomi jempol. Sebelumnya ketiganya 28–36px di semua viewport, sementara
// varian `touch` yang sudah benar tidak pernah dipakai sekali pun (0 dari 246).
const sizeClasses: Record<ButtonSize, string> = {
  sm: "min-h-[44px] px-2.5 text-[12px] md:min-h-0 md:h-7",
  md: "min-h-[44px] px-3.5 text-[13px] md:min-h-0 md:h-8",
  lg: "min-h-[44px] px-5 text-[14px] md:min-h-0 md:h-9",
  // touch: 44px di semua viewport, untuk CTA utama yang juga penting di desktop
  touch: "min-h-[44px] px-5 text-[15px]",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children: React.ReactNode;
}

/**
 * Kelas tombol untuk elemen yang HARUS tetap `<a>`/`<Link>`.
 *
 * Aksi yang berpindah halaman perlu jadi tautan sungguhan supaya klik-tengah,
 * "buka di tab baru", dan "salin alamat tautan" tetap jalan; membungkusnya jadi
 * `<button onClick={router.push}>` menghapus ketiganya. `Button` merender
 * `<button>` dan tidak punya `asChild`, jadi pemakai seperti itu memakai ini.
 */
export function buttonClass({
  variant = "primary",
  size = "md",
  className,
}: { variant?: ButtonVariant; size?: ButtonSize; className?: string } = {}) {
  return cn(BASE_CLASS, variantClasses[variant], sizeClasses[size], className);
}

const BASE_CLASS = cn(
  "inline-flex items-center justify-center gap-2 font-semibold rounded-[var(--radius-md,8px)]",
  "transition-all duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)] cursor-pointer",
  "active:scale-[0.98]",
  "disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/40 focus-visible:ring-offset-2",
);

export function Button({
  variant = "primary",
  size = "md",
  loading,
  className,
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(BASE_CLASS, variantClasses[variant], sizeClasses[size], className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </button>
  );
}
